/**
 * @file CreateMeetingModal.tsx
 * @description Modal component for creating a new meeting.
 * This component collects meeting details from the user and triggers the meeting launch process.
 */

import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useSelector } from "react-redux";
import { selectUserInfo } from "redux/userInfo/userInfoSlice";
import { getMailLists } from "api/canmail/CANmailAPI";
import { sendMeetingInvite, sendMeetingInviteAdHoc } from "./MeetingInviteSender";
import type { AdHocInviteRecipient } from "./MeetingInviteSender";
import { sendScheduleInvite, sendScheduleInviteAdHoc } from "./ScheduleInviteSender";
import { sendMeetingConfirmation } from "./MeetingConfirmationSender";
import RecipientPicker from "components/RecipientPicker";
import { useNavigate } from "react-router-dom";
import {
  createMeeting,
  createScheduledMeeting,
} from "api/meet/meetAPI";
import Uppy from "@uppy/core";
import { UppyFile } from "@uppy/core";
import { DragDrop } from "@uppy/react";
import "@uppy/core/dist/style.css";
import "@uppy/drag-drop/dist/style.min.css";
import useNotificationProvider from "hooks/notificationBar/useNotificationProvider";
import { useCanmeetTourSync } from "components/onboarding/tour/useCanmeetTourSync";
import { setTourGate } from "components/onboarding/tour/tourGate";
import { getActiveTourKey } from "components/onboarding/tour/activeTour";

interface CreateMeetingModalProps {
  open: boolean;
  onClose: () => void;
  onNavigateToListManager: () => void;
  onMeetingScheduled?: () => void;
}

interface MailListObject {
  list_id: number;
  list_name: string;
  list_type_id: number;
  list_type_name: string;
}

const CreateMeetingModal: React.FC<CreateMeetingModalProps> = ({
  open,
  onClose,
  onNavigateToListManager,
  onMeetingScheduled,
}) => {
  const [roomName, setRoomName] = useState<string>("");
  const [autoInvite, setAutoInvite] = useState<boolean>(false);
  const [mailLists, setMailLists] = useState<Array<MailListObject>>([]);
  const [selectedMailList, setSelectedMailList] = useState<string>("");
  // Invite recipients can come from a saved list or an ad-hoc set built here.
  const [inviteMode, setInviteMode] = useState<"list" | "adhoc">("list");
  const [adHocRecipients, setAdHocRecipients] = useState<AdHocInviteRecipient[]>([]);
  const navigate = useNavigate();
  const { notifySuccess, notifyError, notifyWarning } = useNotificationProvider();
  const userInfo = useSelector(selectUserInfo);
  const [meetingMode, setMeetingMode] = useState<"instant" | "schedule">(
    "instant"
  );

  const [attachments, setAttachments] = useState<
    Array<UppyFile<Record<string, unknown>, Record<string, unknown>>>
  >([]);
  const [uppyInstance, setUppyInstance] = useState<Uppy<
    Record<string, unknown>,
    Record<string, unknown>
  > | null>(null);

  // For scheduled meetings
  const [meetingDate, setMeetingDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [duration, setDuration] = useState<number>(60);
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [recurringFrequency, setRecurringFrequency] = useState<
    "daily" | "weekly" | "monthly" | "yearly"
  >("daily");
  const [meetingNotes] = useState<string>("");

  // Advances the CANmeet tours when the dialog opens / the mode is switched,
  // and skips the recurrence step when "Recurring" is off.
  useCanmeetTourSync(open, meetingMode, isRecurring);

  // Publish field validity so tour steps can keep their Next button disabled
  // until the user fills in the meeting name / date / start time.
  useEffect(() => {
    setTourGate("canmeet-name", roomName.trim().length > 0);
    setTourGate("canmeet-date", meetingDate.trim().length > 0);
    setTourGate("canmeet-start-time", startTime.trim().length > 0);
  }, [roomName, meetingDate, startTime]);

  useEffect(() => {
    if (open) {
      getMailLists().then((data) => {
        if (data) {
          setMailLists(data);
        }
      });
    }
  }, [open]);

  // useEffect for Uppy setup
  useEffect(() => {
    if (open) {
      const uppy = new Uppy({
        restrictions: { maxNumberOfFiles: null },
        autoProceed: false,
      });

      // Maximum attachment size (10MB)
      const MAX_ATTACH_MB = 10;

      // Handle file-added event
      uppy.on("file-added", (file) => {
        setAttachments((prev) => {
          const currentTotal = prev.reduce((sum, f) => sum + f.data.size, 0);
          const newTotal = currentTotal + file.data.size;
          const maxTotal = MAX_ATTACH_MB * 1024 * 1024;

          if (file.data.size > maxTotal || newTotal > maxTotal) {
            notifyWarning(`Attachment size cannot exceed ${MAX_ATTACH_MB}MB`);
            uppy.removeFile(file.id);
            return prev;
          }
          return [...prev, file];
        });
      });

      // Handle file-removed event
      uppy.on("file-removed", (file) => {
        setAttachments((prev) => prev.filter((f) => f.id !== file.id));
      });

      setUppyInstance(uppy);

      return () => {
        uppy.close();
        setAttachments([]);
      };
    }
  }, [open]);

  const convertFileToAttachment = (
    file: any
  ): Promise<{ name: string; type: string; data: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file.data);
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          // Remove the "data:*/*;base64," prefix
          const pureBase64 = result.split(",")[1];
          resolve({
            name: file.name,
            type: file.type,
            data: pureBase64,
          });
        } else {
          reject(new Error("File conversion failed."));
        }
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleRoomNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRoomName(e.target.value);
  };

  const handleAutoInviteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAutoInvite(e.target.checked);
  };

  const handleMailListChange = (e: SelectChangeEvent<string>) => {
    setSelectedMailList(e.target.value);
  };

  /**
   * Processes the meeting params, sends the email invite if applicable,
   * and triggers the launch callback.
   */
  const handleLaunch = async () => {
    // Guided tour: don't create a real meeting (no DB row, no invite/confirmation
    // emails, and for instant no navigation into a live call). Just close the
    // dialog — useCanmeetTourSync then advances to the finish slide.
    const tourKey = getActiveTourKey();
    if (
      tourKey === "canmeet_instant_meeting" ||
      tourKey === "canmeet_scheduled_meeting"
    ) {
      notifySuccess("That’s the walkthrough — no meeting was created.");
      onClose();
      return;
    }

    const getDefaultRoomName = () => {
      const today = new Date();
      const formattedDate = today.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      return `Meeting ${formattedDate}`;
    };

    // Use the entered meeting name or fallback to the default.
    const finalMeetingName = roomName.trim()
      ? roomName.trim()
      : getDefaultRoomName();
    const cleanedRoomName =
      finalMeetingName.replace(/\s+/g, "-").replace(/,/g, "") + "-canmeet";

    // Check for autoInvite BEFORE creating the meeting.
    if (autoInvite) {
      if (inviteMode === "adhoc" && adHocRecipients.length === 0) {
        notifyWarning("Add at least one recipient to send the invite.");
        return;
      }
      if (inviteMode === "list" && !selectedMailList) {
        notifyWarning("Please select a mail list to send the invite.");
        return;
      }
    }

    // Process attachments if any
    let processedAttachments: Array<{
      name: string;
      type: string;
      data: string;
    }> = [];
    if (attachments.length > 0) {
      try {
        processedAttachments = await Promise.all(
          attachments.map((file) => convertFileToAttachment(file))
        );
        console.log("Processed attachments:", processedAttachments);
      } catch (error) {
        console.error("[CreateMeetingModal] Failed to process attachments:", error);
        notifyError("Error processing attachments. Please try again.");
        return;
      }
    }

    if (meetingMode === "instant") {
      // Instant Mode: call createMeeting
      try {
        // Ad-hoc invites are list-less: no mail_list_id is stored, so instant-meeting
        // visibility falls back to "anyone with the link" (the emailed join link).
        const mailListIdParam =
          autoInvite && inviteMode === "list" && selectedMailList
            ? parseInt(selectedMailList)
            : null;
        const data = await createMeeting(cleanedRoomName, mailListIdParam);
        const baseUrl = window.location.origin;
        const meetingUrl = `${baseUrl}/canappsv3/meeting?roomUrl=${encodeURIComponent(
          data.roomUrl
        )}`;
        console.log("Meeting Link:", meetingUrl);

        if (autoInvite) {
          if (inviteMode === "adhoc") {
            await sendMeetingInviteAdHoc(
              adHocRecipients,
              cleanedRoomName,
              meetingUrl,
              processedAttachments
            );
          } else {
            await sendMeetingInvite(
              selectedMailList,
              cleanedRoomName,
              meetingUrl,
              processedAttachments
            );
          }
        }

        // Send confirmation email to meeting creator
        try {
          await sendMeetingConfirmation(finalMeetingName, meetingUrl);
          console.log("[CreateMeetingModal] Confirmation email sent");
        } catch (error) {
          console.error("[CreateMeetingModal] Failed to send confirmation email:", error);
          // Don't block the meeting creation if confirmation email fails
        }

        // Navigate to the meeting page.
        navigate(`/meeting?roomUrl=${encodeURIComponent(data.roomUrl)}`);
        onClose();
      } catch (error) {
        console.error("[CreateMeetingModal] Failed to create meeting:", error);
        notifyError("Failed to create meeting. Please try again.");
      }
    } else {
      // Schedule Mode: single API call — backend creates Whereby room + DB row atomically
      // Client-side validation
      if (!meetingDate || !startTime) {
        notifyWarning("Please fill in both meeting date and start time.");
        return;
      }

      const meetingStartDateTime = new Date(`${meetingDate}T${startTime}`);
      if (meetingStartDateTime <= new Date()) {
        notifyWarning("Meeting date and time must be in the future.");
        return;
      }

      try {
        const recurrenceParam = isRecurring ? recurringFrequency : "none";

        const scheduledMeetingData = {
          meetingName: finalMeetingName,
          meetingStartDateTime: meetingStartDateTime.toISOString(),
          meetingDuration: duration,
          meetingRecurrence: recurrenceParam,
          mailListId:
            autoInvite && inviteMode === "list" && selectedMailList
              ? parseInt(selectedMailList)
              : undefined,
          meetingCreator:
            userInfo?.userInfo?.fullname ||
            userInfo?.userInfo?.username ||
            "Unknown User",
          notes: meetingNotes,
        };

        const data = await createScheduledMeeting(scheduledMeetingData);
        console.log("[CreateMeetingModal] Scheduled meeting created:", data);

        const baseUrl = window.location.origin;
        const meetingUrl = `${baseUrl}/canappsv3/meeting?roomUrl=${encodeURIComponent(
          data.roomUrl
        )}`;

        if (autoInvite) {
          if (inviteMode === "adhoc") {
            await sendScheduleInviteAdHoc(
              adHocRecipients,
              cleanedRoomName,
              meetingUrl,
              meetingDate,
              startTime,
              duration,
              recurrenceParam,
              processedAttachments
            );
          } else {
            await sendScheduleInvite(
              selectedMailList,
              cleanedRoomName,
              meetingUrl,
              meetingDate,
              startTime,
              duration,
              recurrenceParam,
              processedAttachments
            );
          }
        }

        // Send confirmation email to meeting creator
        try {
          await sendMeetingConfirmation(
            finalMeetingName,
            meetingUrl,
            meetingDate,
            startTime,
            duration,
            recurrenceParam,
            meetingNotes
          );
          console.log("[CreateMeetingModal] Confirmation email sent");
        } catch (error) {
          console.error("[CreateMeetingModal] Failed to send confirmation email:", error);
        }

        notifySuccess("Meeting scheduled successfully!");
        if (onMeetingScheduled) {
          onMeetingScheduled();
        }
        onClose();
      } catch (error) {
        console.error("[CreateMeetingModal] Failed to schedule meeting:", error);
        notifyError(error instanceof Error ? error.message : "Error scheduling meeting. Please try again.");
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Create Meeting</DialogTitle>
        <ToggleButtonGroup
          data-tour="canmeet-mode-toggle"
          color="primary"
          value={meetingMode}
          exclusive
          onChange={(_e, newMode) => {
            if (newMode !== null) {
              setMeetingMode(newMode);
            }
          }}
          sx={{ mx: 3 }}
        >
          <ToggleButton value="instant">Instant</ToggleButton>
          <ToggleButton value="schedule">Schedule</ToggleButton>
        </ToggleButtonGroup>
        <DialogContent sx={{ minHeight: "300px" }}>
          <Grid container direction="column" spacing={2}>
            {/* Meeting Name Input */}
            <Grid item>
              <TextField
                data-tour="canmeet-name"
                label="Meeting Name"
                variant="outlined"
                fullWidth
                value={roomName}
                onChange={handleRoomNameChange}
                placeholder="Enter a meeting name (optional)"
              />
            </Grid>
            {/* Scheduled Meeting Fields */}
            {meetingMode === "schedule" && (
              <Grid item>
                <Grid container direction="column" spacing={2}>
                  <Grid item>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <TextField
                          data-tour="canmeet-date"
                          label="Meeting Date"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={meetingDate}
                          onChange={(e) => setMeetingDate(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          data-tour="canmeet-start-time"
                          label="Start Time"
                          type="time"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel id="duration-select-label">
                            Duration
                          </InputLabel>
                          <Select
                            data-tour="canmeet-duration"
                            labelId="duration-select-label"
                            label="Duration"
                            value={duration}
                            onChange={(e) =>
                              setDuration(
                                e.target.value as
                                  | 15
                                  | 30
                                  | 45
                                  | 60
                                  | 120
                                  | 180
                                  | 240
                              )
                            }
                          >
                            <MenuItem value={15}>15 min</MenuItem>
                            <MenuItem value={30}>30 min</MenuItem>
                            <MenuItem value={45}>45 min</MenuItem>
                            <MenuItem value={60}>1 hour</MenuItem>
                            <MenuItem value={120}>2 hours</MenuItem>
                            <MenuItem value={180}>3 hours</MenuItem>
                            <MenuItem value={240}>4 hours</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isRecurring}
                          onChange={(e) => setIsRecurring(e.target.checked)}
                          color="primary"
                        />
                      }
                      data-tour="canmeet-recurring"
                      label="Recurring"
                    />
                  </Grid>
                  {isRecurring && (
                    <Grid item>
                      <FormControl fullWidth data-tour="canmeet-recurrence">
                        <InputLabel id="recurrence-select-label">
                          Recurrence
                        </InputLabel>
                        <Select
                          labelId="recurrence-select-label"
                          label="Recurrence"
                          value={recurringFrequency}
                          onChange={(e) =>
                            setRecurringFrequency(
                              e.target.value as
                                | "daily"
                                | "weekly"
                                | "monthly"
                                | "yearly"
                            )
                          }
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {/* Notes Section */}
                  {/* <Grid item>
                    <TextField
                      label="Notes"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      value={meetingNotes}
                      onChange={(e) => setMeetingNotes(e.target.value)}
                      placeholder="Add optional notes"
                    />
                  </Grid> */}
                </Grid>
              </Grid>
            )}

            {/* Auto-Invite Toggle */}
            <Grid item>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoInvite}
                    onChange={handleAutoInviteChange}
                    color="primary"
                  />
                }
                data-tour="canmeet-invite"
                label="Send invite to mail list"
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 4, mt: -0.5 }}
              >
                {!autoInvite
                  ? "Anyone with the link can join this meeting."
                  : inviteMode === "adhoc"
                  ? "Invites are emailed to the recipients you choose. Anyone with the link can join."
                  : "Only people on the mailing list can join this meeting."}
              </Typography>
            </Grid>
            {/* Invite recipients: an existing mail list, or an ad-hoc set built here */}
            {autoInvite && (
              <Grid item>
                <ToggleButtonGroup
                  color="primary"
                  value={inviteMode}
                  exclusive
                  size="small"
                  onChange={(_e, newMode) => {
                    if (newMode !== null) {
                      setInviteMode(newMode);
                      if (newMode === "adhoc") {
                        setSelectedMailList("");
                      } else {
                        setAdHocRecipients([]);
                      }
                    }
                  }}
                  sx={{ mb: 2 }}
                >
                  <ToggleButton value="list">Mailing list</ToggleButton>
                  <ToggleButton value="adhoc">Custom recipients</ToggleButton>
                </ToggleButtonGroup>

                {inviteMode === "adhoc" ? (
                  <RecipientPicker
                    value={adHocRecipients}
                    onChange={setAdHocRecipients}
                    label="Recipients"
                  />
                ) : (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={8}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="mail-list-select-label">
                          Select Mail List
                        </InputLabel>
                        <Select
                          labelId="mail-list-select-label"
                          label="Select Mail List"
                          value={selectedMailList}
                          onChange={handleMailListChange}
                        >
                          {mailLists.map((list) => (
                            <MenuItem
                              key={list.list_id}
                              value={list.list_id.toString()}
                            >
                              {list.list_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="outlined"
                        onClick={onNavigateToListManager}
                        fullWidth
                        size="small"
                      >
                        List Manager
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            )}

            {/* File Upload Section - Only show when autoInvite is true */}
            {autoInvite && (
              <Grid item>
                <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
                  Add Attachments
                </Typography>

                {/* Uppy Drag n Drop */}
                <Box
                  sx={{
                    mb: 2,
                    "& .uppy-DragDrop-container": {
                      height: "150px",
                      overflow: "hidden",
                    },
                    "& .uppy-DragDrop-inner": {
                      padding: "20px 20px",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    },
                  }}
                >
                  {uppyInstance && (
                    <DragDrop
                      uppy={uppyInstance}
                      width="100%"
                      note="Maximum allowed file size: 10 MB"
                    />
                  )}
                </Box>

                {/* Attachment Chips */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {attachments.map((file) => (
                    <Chip
                      key={file.id}
                      label={file.name}
                      onDelete={() => {
                        setAttachments((prev) =>
                          prev.filter((f) => f.id !== file.id)
                        );
                        if (uppyInstance) {
                          uppyInstance.removeFile(file.id);
                        }
                      }}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        {/* Dialog actions: Cancel or Launch Meeting */}
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button onClick={onClose} variant="outlined" color="error">
            Cancel
          </Button>
          <Button data-tour="canmeet-submit" onClick={handleLaunch} variant="contained" color="primary">
            {meetingMode === "instant" ? "Launch Meeting" : "Schedule Meeting"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateMeetingModal;
